/**
 * How an inventory item's actions are PLAYED - one component per mechanic (backend inventory.py MECHANICS).
 * They all drive the same engine actions, so what's recorded (and checked) is the same whichever way it's played:
 *   confirm  - a button                          sign    - draw a signature, then sign
 *   choose   - pick an option                    compose - fill the line's blanks from word chips; written by hand
 *   pick     - take one of several look-alikes (bottles tinted from one picture, or folder tabs), each labelled
 *   place    - drag the thing onto the right slot (hooks, drawers, a tray) - or tap it, then tap the slot
 *   keypad   - type the code                     spot    - tap the problem on the camera feed
 *   amount   - − / +                             pour    - hold to pour, release at the mark
 *   dial     - turn the dial to the amount       hold    - press and hold until it's done (lower the forks)
 * Looks: compose `form` (a system's form: a field per blank), pick `contacts` (a phone directory) / `folder` (open it to
 * check the details before taking it) / `bottle` (label lines), pour `scale` (scoop onto a scale with a readout).
 * A composed line or a typed code that matches none of the options plays the hidden "(something else)" action.
 * `acts` are the engine actions of this item-action (for choices: one per option, `option` = its text).
 */
import React, { useEffect, useRef, useState } from 'react';

const norm = (t) => String(t ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
const visible = (acts) => acts.filter((x) => !x.hidden);
const other = (acts) => acts.find((x) => x.hidden);
const byText = (acts, text) => acts.find((x) => !x.hidden && norm(x.option) === norm(text)) || other(acts);

/** Text with the answer key's values picked out (where a call or a document tells them). */
export function Highlighted({ text, facts = [] }) {
  const keys = [...facts].filter(Boolean).sort((a, b) => b.length - a.length);
  if (!keys.length) return <>{text}</>;
  const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = String(text).split(new RegExp(`(${keys.map(escape).join('|')})`, 'g'));
  return <>{parts.map((p, i) => (keys.includes(p) ? <mark key={i} className="mg-fact">{p}</mark> : <React.Fragment key={i}>{p}</React.Fragment>))}</>;
}

/** A caller's words appear as they're spoken. */
export function Spoken({ text, facts }) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    setShown(0);
    const t = setInterval(() => setShown((n) => (n >= text.length ? (clearInterval(t), n) : n + 2)), 30);
    return () => clearInterval(t);
  }, [text]);
  return (
    <p className="mg-inv-says mg-spoken" aria-label={text}>
      {shown >= text.length ? <Highlighted text={text} facts={facts} /> : text.slice(0, shown)}
    </p>
  );
}

export function Confirm({ a, acts, attempt }) {
  return acts.map((x) => <button key={x.id} type="button" className="mg-btn mg-inv-confirm" onClick={() => attempt(x)}>{a.label}</button>);
}

export function Choose({ a, acts, value, attempt }) {
  return (
    <div className="mg-inv-choose" role="group" aria-label={a.label}>
      <span className="mg-inv-ask">{a.label}:</span>
      {visible(acts).map((x) => (
        <button key={x.id} type="button" className={`mg-inv-option ${value === x.option ? 'is-picked' : ''}`}
          aria-pressed={value === x.option} onClick={() => attempt(x)}>{x.option}</button>
      ))}
    </div>
  );
}

/** Fill the blanks from chips (the right value and look-alikes); "Write" puts it on the page by hand. */
export function Compose({ a, acts, value, attempt }) {
  const frame = a.frame;
  const [picked, setPicked] = useState(() => frame.blanks.map((b) => (b.chips.length === 1 ? b.chips[0] : null)));
  const ready = picked.every((p) => p !== null);
  const line = frame.parts.map((part, i) => part + (i < picked.length ? (picked[i] ?? '') : '')).join('');
  if (a.look === 'form') return <Form a={a} acts={acts} value={value} attempt={attempt} picked={picked} setPicked={setPicked} ready={ready} line={line} />;
  return (
    <div className="mg-compose" role="group" aria-label={a.label}>
      <span className="mg-inv-ask">{a.label}:</span>
      <div className="mg-compose-line">
        {frame.parts.map((part, i) => (
          <React.Fragment key={i}>
            {part && <span>{part}</span>}
            {i < frame.blanks.length && (
              <span className={`mg-blank ${picked[i] ? 'is-filled' : ''}`}>{picked[i] || '_____'}</span>
            )}
          </React.Fragment>
        ))}
      </div>
      {frame.blanks.map((b, i) => b.chips.length > 1 && (
        <div key={`${i}-${b.fact}`} className="mg-chips-row" role="group" aria-label={`Choose: ${b.label || b.fact}`}>
          {b.chips.map((c) => (
            <button key={c} type="button" className={`mg-word ${picked[i] === c ? 'is-picked' : ''}`} aria-pressed={picked[i] === c}
              onClick={() => setPicked((p) => p.map((v, k) => (k === i ? c : v)))}>{c}</button>
          ))}
        </div>
      ))}
      <button type="button" className="mg-btn mg-inv-confirm" disabled={!ready} onClick={() => attempt(byText(acts, line))}>✍️ {a.label}</button>
      {value !== null && value !== undefined && <p className="mg-hand" aria-label="Written">{value}</p>}
    </div>
  );
}

/** A system's form: one field per blank (the right value and look-alikes to choose from), then Submit. */
function Form({ a, acts, value, attempt, picked, setPicked, ready, line }) {
  const frame = a.frame;
  return (
    <form className="mg-form" aria-label={a.label} onSubmit={(e) => { e.preventDefault(); if (ready) attempt(byText(acts, line)); }}>
      {frame.blanks.map((b, i) => (
        <fieldset key={`${i}-${b.fact}`} className="mg-form-field">
          <legend>{(b.label || b.fact).replace(/^\w/, (c) => c.toUpperCase())}</legend>
          {b.chips.length === 1 ? <span className="mg-form-fixed">{b.chips[0]}</span> : b.chips.map((c) => (
            <button key={c} type="button" className={`mg-form-opt ${picked[i] === c ? 'is-picked' : ''}`} aria-pressed={picked[i] === c}
              onClick={() => setPicked((p) => p.map((v, k) => (k === i ? c : v)))}>{c}</button>
          ))}
        </fieldset>
      ))}
      <div className="mg-form-preview" aria-live="polite">{ready ? line : '…'}</div>
      <button type="submit" className="mg-btn mg-inv-confirm" disabled={!ready}>{a.label}</button>
      {value !== null && value !== undefined && <p className="mg-form-sent">✓ Sent: {value}</p>}
    </form>
  );
}

/** "R. Davis · DOB 12/03/1961" -> ["R. Davis", "DOB 12/03/1961"]: a label's or a folder's lines. */
const linesOf = (text) => String(text ?? '').split(/\s+·\s+/).filter(Boolean);
const initials = (name) => name.split(/\s+/).filter((w) => /^[A-Z]/.test(w)).slice(0, 2).map((w) => w[0]).join('') || '☎';

/** A phone directory: each contact "Name - role"; call the right one. */
function Contacts({ a, acts, value, attempt }) {
  return (
    <div className="mg-contacts" role="list" aria-label={a.label}>
      {visible(acts).map((x) => {
        const [who, role] = String(x.option).split(/\s+[-–]\s+/);
        return (
          <div key={x.id} role="listitem" className={`mg-contact ${value === x.option ? 'is-picked' : ''}`}>
            <span className="mg-contact-face" aria-hidden="true">{initials(who)}</span>
            <span className="mg-contact-who"><strong>{who}</strong>{role && <small>{role}</small>}</span>
            <button type="button" className="mg-btn mg-contact-call" aria-label={`${a.label} ${x.option}`} onClick={() => attempt(x)}>📞 {a.label}</button>
          </div>
        );
      })}
    </div>
  );
}

/** Folders: open one to read what's inside, then take it - or put it back and open another. */
function Folders({ a, acts, value, attempt }) {
  const [opened, setOpened] = useState(null);
  const shown = visible(acts).find((x) => x.id === opened);
  return (
    <div className="mg-folders">
      <div className="mg-pick-row is-folder">
        {visible(acts).map((x) => (
          <button key={x.id} type="button" className={`mg-pick-thing ${value === x.option ? 'is-picked' : ''} ${opened === x.id ? 'is-open' : ''}`}
            aria-pressed={opened === x.id} aria-label={`Open ${linesOf(x.option)[0]}`} onClick={() => setOpened(x.id)}>
            <span className="mg-folder"><span className="mg-folder-tab">{linesOf(x.option)[0]}</span></span>
          </button>
        ))}
      </div>
      {shown && (
        <div className="mg-folder-open" role="region" aria-label="Inside the folder">
          <strong>{linesOf(shown.option)[0]}</strong>
          {linesOf(shown.option).slice(1).map((l) => <span key={l}>{l}</span>)}
          <div className="mg-folder-actions">
            <button type="button" className="mg-btn" onClick={() => attempt(shown)}>{a.label}</button>
            <button type="button" className="mg-link-btn" onClick={() => setOpened(null)}>Put it back</button>
          </div>
        </div>
      )}
    </div>
  );
}

const BANDS = { red: '#c0392b', cream: '#f3e3b5', black: '#1e1e1e', blue: '#2f5fa8', yellow: '#e2b93b', green: '#3f8f4f' };

const TINTS = ['none', 'hue-rotate(80deg) saturate(0.6)', 'hue-rotate(190deg) saturate(0.5)', 'hue-rotate(320deg) saturate(0.7)', 'sepia(0.6)'];

/** Take one of several look-alikes - bottles tinted from the item's option picture, or folders - each labelled. */
export function Pick({ a, acts, value, attempt, optionSrc, asset }) {
  if (a.look === 'contacts') return <Contacts a={a} acts={acts} value={value} attempt={attempt} />;
  if (a.look === 'folder') return <Folders a={a} acts={acts} value={value} attempt={attempt} />;
  if (a.look === 'extinguisher') {
    // red canisters told apart by the colour band near the top ("CO2 (black)")
    return (
      <div className="mg-pick" role="group" aria-label={a.label}>
        <span className="mg-inv-ask">{a.label}:</span>
        <div className="mg-pick-row is-extinguisher">
          {visible(acts).map((x) => {
            const band = (/\(([a-z ]+)\)/i.exec(x.option) || [null, 'red'])[1].trim().toLowerCase();
            return (
              <button key={x.id} type="button" className={`mg-pick-thing ${value === x.option ? 'is-picked' : ''}`} aria-pressed={value === x.option}
                aria-label={x.option} onClick={() => attempt(x)}>
                <span className="mg-extinguisher" aria-hidden="true"><i className="mg-ext-band" style={{ background: BANDS[band] || band }} /></span>
                <span className="mg-pick-caption">{x.option.replace(/\s*\(.*\)\s*$/, '')}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  if (a.look === 'container') {
    return (
      <div className="mg-pick" role="group" aria-label={a.label}>
        <span className="mg-inv-ask">{a.label}:</span>
        <div className="mg-pick-row is-container">
          {visible(acts).map((x) => (
            <button key={x.id} type="button" className={`mg-pick-thing ${value === x.option ? 'is-picked' : ''}`} aria-pressed={value === x.option}
              aria-label={x.option} onClick={() => attempt(x)}>
              <span className="mg-tub"><i className="mg-tub-lid" /><span className="mg-tub-label">
                {linesOf(x.option).map((l, k) => <span key={l} className={k ? 'mg-bottle-small' : ''}>{l}</span>)}</span></span>
            </button>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="mg-pick" role="group" aria-label={a.label}>
      <span className="mg-inv-ask">{a.label}:</span>
      <div className={`mg-pick-row is-${a.look || 'bottle'}`}>
        {visible(acts).map((x, i) => (
          <button key={x.id} type="button" className={`mg-pick-thing ${value === x.option ? 'is-picked' : ''}`}
            aria-pressed={value === x.option} aria-label={x.option} onClick={() => attempt(x)}>
            {a.look === 'folder'
              ? <span className="mg-folder"><span className="mg-folder-tab">{x.option}</span></span>
              : (
                <span className="mg-bottle">
                  {optionSrc ? <img src={asset(optionSrc)} alt="" style={{ filter: TINTS[i % TINTS.length] }} /> : <span className="mg-bottle-glass" />}
                  <span className="mg-bottle-label">{linesOf(x.option).map((l, k) => <span key={l} className={k ? 'mg-bottle-small' : ''}>{l}</span>)}</span>
                </span>
              )}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Drag the thing onto the right slot - hooks, drawers or a tray (or tap it, then tap the slot). */
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const slotOf = (text) => { const m = /^(\w{3})\w*\s+(\d{1,2}:\d{2})/.exec(String(text || '').trim()); return m ? { day: m[1], time: m[2].padStart(5, '0') } : null; };

/** A week to book into: the free and booked slots in a grid; tap the right one (booked ones show who's there). */
function CalendarPlace({ a, acts, value, attempt, text }) {
  const booked = Object.fromEntries(linesOf(text?.week).map((row) => { const [when, ...who] = row.split(/\s+-\s+/); const at = slotOf(when); return at ? [`${at.day} ${at.time}`, who.join(' - ')] : null; }).filter(Boolean));
  const cells = visible(acts).map((x) => ({ x, at: slotOf(x.option) })).filter((c) => c.at);
  const all = [...cells.map((c) => c.at), ...Object.keys(booked).map((k) => ({ day: k.slice(0, 3), time: k.slice(4) }))];
  const days = DAYS.filter((d) => all.some((c) => c.day === d));
  const times = [...new Set(all.map((c) => c.time))].sort();
  return (
    <div className="mg-cal-book" role="grid" aria-label={a.label} style={{ gridTemplateColumns: `auto repeat(${days.length}, 1fr)` }}>
      <span />
      {days.map((d) => <strong key={d} role="columnheader">{d}</strong>)}
      {times.map((t) => (
        <React.Fragment key={t}>
          <strong role="rowheader">{t}</strong>
          {days.map((d) => {
            const cell = cells.find((c) => c.at.day === d && c.at.time === t);
            const who = booked[`${d} ${t}`];
            if (!cell) return <span key={d} className={`mg-cal-cell ${who ? 'is-booked' : 'is-none'}`}>{who || ''}</span>;
            return (
              <button key={d} type="button" className={`mg-cal-cell ${who ? 'is-booked' : 'is-free'} ${value === cell.x.option ? 'is-filled' : ''}`}
                aria-label={`${a.label} ${cell.x.option}${who ? ` (booked: ${who})` : ''}`} onClick={() => attempt(cell.x)}>
                {value === cell.x.option ? '✓ ' : ''}{who || ''}
              </button>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
}

/** A shelving rack: bays A1..C4 in a grid; tap the right bay. */
function RackPlace({ a, acts, value, attempt }) {
  const at = (o) => { const m = /^([A-Z])\s*-?\s*(\d+)$/i.exec(String(o).trim()); return m ? [m[1].toUpperCase(), Number(m[2])] : null; };
  const spots = visible(acts).map((x) => ({ x, at: at(x.option) })).filter((c) => c.at);
  const rows = [...new Set(['A', 'B', 'C', ...spots.map((c) => c.at[0])])].sort();
  const cols = Math.max(4, ...spots.map((c) => c.at[1]));
  return (
    <div className="mg-rack" role="grid" aria-label={a.label} style={{ gridTemplateColumns: `auto repeat(${cols}, 1fr)` }}>
      {rows.map((r) => (
        <React.Fragment key={r}>
          <strong role="rowheader">{r}</strong>
          {Array.from({ length: cols }, (_, i) => i + 1).map((n) => {
            const c = spots.find((y) => y.at[0] === r && y.at[1] === n);
            return c ? (
              <button key={n} type="button" className={`mg-rack-bay ${value === c.x.option ? 'is-filled' : ''}`} aria-label={`${a.label} ${r}${n}`}
                onClick={() => attempt(c.x)}>{r}{n}{value === c.x.option ? ' 📦' : ''}</button>
            ) : <span key={n} className="mg-rack-bay is-other">{r}{n}</span>;
          })}
        </React.Fragment>
      ))}
    </div>
  );
}

export function Place({ a, acts, value, attempt, icon, text }) {
  if (a.look === 'calendar') return <CalendarPlace a={a} acts={acts} value={value} attempt={attempt} text={text} />;
  if (a.look === 'rack') return <RackPlace a={a} acts={acts} value={value} attempt={attempt} />;
  return <PlaceInSlot a={a} acts={acts} value={value} attempt={attempt} icon={icon} />;
}

function PlaceInSlot({ a, acts, value, attempt, icon }) {
  const [held, setHeld] = useState(false);
  const [drag, setDrag] = useState(null);       // {x, y} while dragging
  const slots = useRef({});
  const drop = (x) => { setHeld(false); setDrag(null); attempt(x); };
  const onUp = (e) => {
    if (!drag) return;
    const hit = visible(acts).find((x) => {
      const r = slots.current[x.id]?.getBoundingClientRect();
      return r && e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
    });
    setDrag(null);
    if (hit) drop(hit);
  };
  return (
    <div className="mg-place" role="group" aria-label={a.label} onPointerMove={(e) => drag && setDrag({ x: e.clientX, y: e.clientY })} onPointerUp={onUp}>
      <span className="mg-inv-ask">{a.label}: <span className="mg-muted-note">drag it, or tap it then tap where it goes</span></span>
      <button type="button" className={`mg-token ${held ? 'is-held' : ''}`} aria-pressed={held} aria-label="The thing to put away"
        onClick={() => setHeld((h) => !h)} onPointerDown={(e) => { e.currentTarget.releasePointerCapture?.(e.pointerId); setDrag({ x: e.clientX, y: e.clientY }); }}>
        {icon}
      </button>
      {drag && <span className="mg-token-ghost" style={{ left: drag.x, top: drag.y }}>{icon}</span>}
      <div className={`mg-slots is-${a.look || 'tray'}`}>
        {visible(acts).map((x) => (
          <button key={x.id} ref={(el) => { slots.current[x.id] = el; }} type="button"
            className={`mg-slot-box ${value === x.option ? 'is-filled' : ''} ${held ? 'is-open' : ''}`}
            onClick={() => (held ? drop(x) : setHeld(false))} aria-label={`${a.label} ${x.option}`}>
            {a.look === 'hooks' && <span className="mg-hook" aria-hidden="true" />}
            <span className="mg-slot-tag">{x.option}</span>
            {value === x.option && <span className="mg-slot-in" aria-hidden="true">{icon}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Type the code on keys. */
export function Keypad({ a, acts, value, attempt }) {
  const [typed, setTyped] = useState('');
  const digits = visible(acts).every((x) => /^[0-9]+$/.test(String(x.option)));
  if (!digits) return <Choose a={a} acts={acts} value={value} attempt={attempt} />;
  const press = (k) => setTyped((t) => (t.length < 8 ? t + k : t));
  return (
    <div className="mg-keypad" role="group" aria-label={a.label}>
      <span className="mg-inv-ask">{a.label}:</span>
      <div className={`mg-keypad-display ${value !== null && value !== undefined ? 'is-set' : ''}`} aria-live="polite">{typed || (value ?? '') || '____'}</div>
      <div className="mg-keys">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9', 'C', '0', '↵'].map((k) => (
          <button key={k} type="button" className={`mg-key ${k === '↵' ? 'is-enter' : ''}`} aria-label={k === '↵' ? 'Enter' : k === 'C' ? 'Clear' : k}
            onClick={() => (k === 'C' ? setTyped('') : k === '↵' ? (typed && (attempt(byText(acts, typed)), setTyped(''))) : press(k))}>{k}</button>
        ))}
      </div>
    </div>
  );
}

const SPOT_ICONS = [[/pallet|box|crate|stack/i, '📦'], [/door|exit/i, '🚪'], [/forklift|truck|vehicle/i, '🚜'], [/person|someone|man|woman|stranger/i, '🧍'],
  [/clear|nothing|fine|empty/i, '✅'], [/fire|smoke/i, '🔥'], [/spill|water|leak/i, '💧'], [/light|lamp/i, '💡'], [/key/i, '🔑']];
const spotIcon = (t) => (SPOT_ICONS.find(([re]) => re.test(t)) || [null, '❔'])[1];

/** Tap the problem on the camera feed - or (look `document`) circle the wrong line on a form. */
export function Spot({ a, acts, value, attempt }) {
  const split = (o) => { const at = String(o).indexOf(': '); return at > 0 ? [o.slice(0, at), o.slice(at + 2)] : ['', o]; };
  if (a.look === 'email') {
    // an email window: its header lines, its text, its link - tap the one that gives it away
    return (
      <div className="mg-email" role="group" aria-label={a.label}>
        <span className="mg-inv-ask">{a.label}:</span>
        {visible(acts).map((x) => {
          const [field, text] = split(x.option);
          const link = /^link$/i.test(field);
          return (
            <button key={x.id} type="button" className={`mg-email-line is-${field.toLowerCase() || 'text'} ${value === x.option ? 'is-circled' : ''}`}
              aria-pressed={value === x.option} aria-label={`${a.label}: ${x.option}`} onClick={() => attempt(x)}>
              {field && !/^text$/i.test(field) && <span className="mg-email-field">{field}</span>}
              {link ? <u>{text}</u> : <span>{text}</span>}
            </button>
          );
        })}
      </div>
    );
  }
  if (a.look === 'tags') {
    return (
      <div className="mg-shelf-edge" role="group" aria-label={a.label}>
        <span className="mg-inv-ask">{a.label}:</span>
        <div className="mg-tags-row">
          {visible(acts).map((x) => {
            const [what, price] = linesOf(x.option);
            return (
              <button key={x.id} type="button" className={`mg-price-tag ${value === x.option ? 'is-circled' : ''}`} aria-pressed={value === x.option}
                aria-label={`${a.label}: ${x.option}`} onClick={() => attempt(x)}>
                <small>{what}</small><strong>{price}</strong>
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  if (a.look === 'document') {
    return (
      <div className="mg-paper" role="group" aria-label={a.label}>
        <span className="mg-inv-ask">{a.label}:</span>
        {visible(acts).map((x) => {
          const at = String(x.option).indexOf(': ');
          return (
            <button key={x.id} type="button" className={`mg-paper-line ${value === x.option ? 'is-circled' : ''}`} aria-pressed={value === x.option}
              aria-label={`${a.label}: ${x.option}`} onClick={() => attempt(x)}>
              {at > 0 ? <><span>{x.option.slice(0, at)}</span><strong>{x.option.slice(at + 2)}</strong></> : <strong>{x.option}</strong>}
            </button>
          );
        })}
      </div>
    );
  }
  return (
    <div className="mg-spot" role="group" aria-label={a.label}>
      <span className="mg-inv-ask">{a.label}:</span>
      <div className="mg-feed">
        <span className="mg-feed-rec">● REC</span>
        {visible(acts).map((x) => (
          <button key={x.id} type="button" className={`mg-feed-zone ${value === x.option ? 'is-marked' : ''}`} aria-pressed={value === x.option}
            onClick={() => attempt(x)}>
            <span className="mg-feed-icon" aria-hidden="true">{spotIcon(x.option)}</span>
            <span className="mg-feed-caption">{x.option}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

const sortByValue = (acts) => acts.slice().sort((x, y) => x.value - y.value);

export function Amount({ a, acts, value, attempt }) {
  const steps = sortByValue(acts);
  const at = steps.findIndex((x) => x.value === value);
  const lower = at > 0 ? steps[at - 1] : null;
  const higher = at === -1 ? steps.find((x) => x.value > 0) || steps[0] : steps[at + 1] || null;
  return (
    <div className="mg-inv-amount">
      <span className="mg-inv-ask">{a.label}:</span>
      <button type="button" className="mg-step" disabled={!lower} onClick={() => lower && attempt(lower)}>−</button>
      <span className="mg-meter-value">{value ?? 0} {a.unit || ''}</span>
      <button type="button" className="mg-step" disabled={!higher} onClick={() => higher && attempt(higher)}>+</button>
    </div>
  );
}

/** Hold to pour: the level climbs step by step while held; release to stop. Pour out to start again. */
export function Pour({ a, acts, value, attempt }) {
  const steps = sortByValue(acts);
  const timer = useRef(null);
  const current = useRef(value ?? 0);
  current.current = value ?? 0;
  const next = () => steps.find((x) => x.value > current.current);
  const stop = () => { clearInterval(timer.current); timer.current = null; };
  const start = () => {
    if (timer.current) return;
    const n = next();
    if (n) attempt(n);
    timer.current = setInterval(() => { const m = next(); if (m) attempt(m); else stop(); }, 420);
  };
  useEffect(() => stop, []);
  const max = Math.max(1, ...steps.map((x) => x.value));
  const holdBtn = (label) => (
    <button type="button" className="mg-btn mg-pour-btn" onPointerDown={start} onPointerUp={stop} onPointerLeave={stop}
      onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); const n = next(); if (n) attempt(n); } }}>
      {label}
    </button>
  );
  if (a.look === 'syringe') {
    return (
      <div className="mg-pour mg-syringe-set">
        <div className="mg-syringe" aria-hidden="true">
          <div className="mg-syringe-barrel">
            {steps.filter((x) => x.value > 0).map((x) => <span key={x.id} className="mg-syringe-mark" style={{ left: `${(100 * x.value) / max}%` }}>{x.value}</span>)}
            <i style={{ width: `${(100 * (value ?? 0)) / max}%` }} />
          </div>
          <div className="mg-syringe-plunger" style={{ left: `${(100 * (value ?? 0)) / max}%` }} />
        </div>
        <div className="mg-closeup-side">
          <span className="mg-meter-value" aria-live="polite">{value ?? 0} {a.unit || ''}</span>
          {holdBtn('Hold to draw up')}
          <button type="button" className="mg-btn mg-btn-quiet" disabled={!value} onClick={() => attempt(steps[0])}>Push it back out</button>
        </div>
      </div>
    );
  }
  if (a.look === 'scale') {
    return (
      <div className="mg-pour mg-scale">
        <div className="mg-scale-body" aria-hidden="true">
          <div className="mg-scale-heap" style={{ height: `${(70 * (value ?? 0)) / max}%` }} />
          <div className="mg-scale-plate" />
          <div className="mg-scale-readout">{(value ?? 0)} {a.unit || ''}</div>
        </div>
        <div className="mg-closeup-side">
          <span className="mg-meter-value" aria-live="polite">{value ?? 0} {a.unit || ''}</span>
          {holdBtn('Hold to scoop on')}
          <button type="button" className="mg-btn mg-btn-quiet" disabled={!value} onClick={() => attempt(steps[0])}>Empty the scale</button>
        </div>
      </div>
    );
  }
  return (
    <div className="mg-pour">
      <div className="mg-pour-glass" aria-hidden="true">
        {steps.filter((x) => x.value > 0).map((x) => <span key={x.id} className="mg-pour-mark" style={{ bottom: `${(100 * x.value) / max}%` }}>{x.value}</span>)}
        <i style={{ height: `${(100 * (value ?? 0)) / max}%` }} />
      </div>
      <div className="mg-closeup-side">
        <span className="mg-meter-value" aria-live="polite">{value ?? 0} {a.unit || ''}</span>
        <button type="button" className="mg-btn mg-pour-btn" onPointerDown={start} onPointerUp={stop} onPointerLeave={stop}
          onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); const n = next(); if (n) attempt(n); } }}>
          Hold to pour
        </button>
        <button type="button" className="mg-btn mg-btn-quiet" disabled={!value} onClick={() => attempt(steps[0])}>Pour it out</button>
      </div>
    </div>
  );
}

/** Turn the dial: click a mark (or use − / +). */
export function Dial({ a, acts, value, attempt }) {
  const steps = sortByValue(acts);
  const angle = (i) => -120 + (240 * i) / Math.max(1, steps.length - 1);
  const at = Math.max(0, steps.findIndex((x) => x.value === value));
  const point = (deg, r) => [100 + r * Math.sin((deg * Math.PI) / 180), 100 - r * Math.cos((deg * Math.PI) / 180)];
  const [nx, ny] = point(angle(value === null || value === undefined ? 0 : at), 62);
  return (
    <div className="mg-dial-set">
      <span className="mg-inv-ask">{a.label}:</span>
      <svg viewBox="0 0 200 150" className="mg-dial-svg" role="group" aria-label={a.label}>
        <path d="M30 140 A80 80 0 1 1 170 140" fill="#fdf6e3" stroke="#3a2d17" strokeWidth="4" />
        {steps.map((x, i) => {
          const [tx, ty] = point(angle(i), 84);
          const [lx, ly] = point(angle(i), 70);
          return (
            <g key={x.id} className="mg-dial-tick" onClick={() => attempt(x)} role="button" aria-label={`${x.value} ${a.unit || ''}`}>
              <line x1={lx} y1={ly} x2={tx} y2={ty} stroke="#3a2d17" strokeWidth="3" />
              <text x={point(angle(i), 52)[0]} y={point(angle(i), 52)[1] + 4} textAnchor="middle" fontSize="11">{x.value}</text>
            </g>
          );
        })}
        <line x1="100" y1="100" x2={nx} y2={ny} stroke="#b5432f" strokeWidth="4" strokeLinecap="round" className="mg-dial-needle" />
        <circle cx="100" cy="100" r="6" fill="#1e1a15" />
      </svg>
      <Amount a={{ ...a, label: 'Fine-tune' }} acts={acts} value={value} attempt={attempt} />
    </div>
  );
}

/** Draw a signature (a few strokes), then sign. */
export function Sign({ a, acts, attempt }) {
  const canvas = useRef(null);
  const drawing = useRef(false);
  const [ink, setInk] = useState(0);
  const pos = (e) => { const r = canvas.current.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
  const pen = () => (canvas.current && canvas.current.getContext ? canvas.current.getContext('2d') : null);   // none in some test browsers
  const down = (e) => { drawing.current = true; const ctx = pen(); const [x, y] = pos(e); if (ctx) { ctx.beginPath(); ctx.moveTo(x, y); } };
  const move = (e) => {
    if (!drawing.current) return;
    const ctx = pen();
    const [x, y] = pos(e);
    if (ctx) { ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.strokeStyle = '#1e3a8a'; ctx.lineTo(x, y); ctx.stroke(); }
    setInk((n) => n + 1);
  };
  return (
    <div className="mg-sign" role="group" aria-label={a.label}>
      <span className="mg-inv-ask">{a.label}: <span className="mg-muted-note">sign in the box</span></span>
      <canvas ref={canvas} width={320} height={90} className="mg-sign-pad" aria-label="Signature box"
        onPointerDown={down} onPointerMove={move} onPointerUp={() => { drawing.current = false; }} onPointerLeave={() => { drawing.current = false; }} />
      {acts.map((x) => <button key={x.id} type="button" className="mg-btn" disabled={ink < 8} onClick={() => attempt(x)}>{a.label}</button>)}
      <button type="button" className="mg-link-btn" onClick={() => { const ctx = pen(); if (ctx) ctx.clearRect(0, 0, 320, 90); setInk(0); }}>Clear</button>
    </div>
  );
}

// ---- several things, each with its right value (an `each` slot): `values[k]` is where thing k is now;
// `acts` hold one engine action per thing per value (x.thing, x.option).
const actFor = (acts, k, v) => acts.find((x) => x.thing === k && x.option === v);

/** Drag each thing into the right bin (or tap it, then tap the bin). Tap one in a bin to take it out again. */
export function Sort({ a, acts, values = [], attempt }) {
  const [held, setHeld] = useState(null);
  if (!a.things?.length || !a.values?.length) return null;    // nothing listed in this mission
  const drop = (bin) => { if (held === null) return; const x = actFor(acts, held, bin); setHeld(null); if (x) attempt(x); };
  const token = (k) => (
    <button key={k} type="button" className={`mg-sort-thing ${held === k ? 'is-held' : ''}`} aria-pressed={held === k}
      draggable onDragStart={() => setHeld(k)} onClick={() => setHeld((h) => (h === k ? null : k))}>{a.things[k]}</button>
  );
  const loose = a.things.map((_, k) => k).filter((k) => values[k] === null || values[k] === undefined);
  return (
    <div className="mg-sort" role="group" aria-label={a.label}>
      <span className="mg-inv-ask">{a.label}: <span className="mg-muted-note">tap one, then the bin it goes in</span></span>
      {loose.length > 0 && <div className="mg-sort-loose">{loose.map(token)}</div>}
      <div className="mg-sort-bins">
        {a.values.map((bin) => (
          <div key={bin} role="button" tabIndex={0} aria-label={`Put it in ${bin}`} className={`mg-sort-bin ${held !== null ? 'is-open' : ''}`}
            onClick={() => drop(bin)} onKeyDown={(e) => { if (e.key === 'Enter') drop(bin); }}
            onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); drop(bin); }}>
            <strong>{bin}</strong>
            {a.things.map((_, k) => k).filter((k) => values[k] === bin).map((k) => (
              <span key={k} onClick={(e) => e.stopPropagation()}>{token(k)}</span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** A row of switches or knobs: flip each one; it shows where it is now. */
export function Switches({ a, acts, values = [], attempt }) {
  if (!a.things?.length || !a.values?.length) return null;    // nothing listed in this mission
  return (
    <div className="mg-switches" role="group" aria-label={a.label}>
      {a.things.map((name, k) => {
        const now = values[k] ?? a.values[a.values.length - 1];
        const on = now === a.values[0];
        const other = a.values.find((v) => v !== now);
        return (
          <div key={name} className="mg-switch-row">
            <span>{name}</span>
            <button type="button" role="switch" aria-checked={on} aria-label={name} className={`mg-switch ${on ? 'is-on' : 'is-off'}`}
              onClick={() => { const x = actFor(acts, k, other); if (x) attempt(x); }}>
              <i aria-hidden="true" /><small>{now}</small>
            </button>
          </div>
        );
      })}
    </div>
  );
}

/** Take exactly the right things: tap to take one into your hands, tap again to put it back. */
export function ChooseSeveral({ a, acts, values = [], attempt }) {
  if (!a.things?.length || !a.values?.length) return null;    // nothing listed in this mission
  const [take, leave] = a.values;
  const flip = (k) => { const x = actFor(acts, k, values[k] === take ? leave : take); if (x) attempt(x); };
  if (a.look === 'checklist') {
    return (
      <div className="mg-checklist" role="group" aria-label={a.label}>
        {a.things.map((name, k) => (
          <label key={name} className="mg-check-line">
            <input type="checkbox" checked={values[k] === take} onChange={() => flip(k)} />
            <span>{name}</span>
          </label>
        ))}
      </div>
    );
  }
  const tile = (k) => (
    <button key={k} type="button" className={`mg-kit-thing ${values[k] === take ? 'is-taken' : ''}`} aria-pressed={values[k] === take}
      onClick={() => flip(k)}>{a.things[k]}</button>
  );
  const all = a.things.map((_, k) => k);
  return (
    <div className="mg-kit" role="group" aria-label={a.label}>
      <span className="mg-inv-ask">{a.label}:</span>
      <div className="mg-kit-box">{all.filter((k) => values[k] !== take).map(tile)}</div>
      <div className="mg-kit-hands" aria-label="In your hands">
        <span className="mg-muted-note">🤲 In your hands</span>
        {all.filter((k) => values[k] === take).map(tile)}
      </div>
    </div>
  );
}

/** Put them in order: tap them in the order they should be done; "Start again" to redo it. */
export function Sequence({ a, acts, values = [], attempt }) {
  const [round, setRound] = useState([]);       // the things tapped so far, this time round
  if (!a.things?.length || !a.values?.length) return null;    // nothing listed in this mission
  const tap = (k) => {
    if (round.includes(k)) return;
    const x = actFor(acts, k, a.values[round.length]);
    if (x) { setRound((r) => [...r, k]); attempt(x); }
  };
  const order = a.things.map((_, k) => k).sort((p, q) => (Number(values[p]) || 99) - (Number(values[q]) || 99));
  return (
    <div className="mg-seq" role="group" aria-label={a.label}>
      <span className="mg-inv-ask">{a.label}: <span className="mg-muted-note">tap them in order</span></span>
      <div className="mg-seq-rail">
        {order.map((k) => (
          <button key={k} type="button" className={`mg-ticket ${values[k] ? 'is-placed' : ''}`} aria-label={`${a.things[k]}${values[k] ? ` - number ${values[k]}` : ''}`}
            onClick={() => tap(k)} disabled={round.includes(k)}>
            {values[k] && <b className="mg-ticket-no">{values[k]}</b>}
            <span>{a.things[k]}</span>
          </button>
        ))}
      </div>
      <button type="button" className="mg-link-btn" disabled={!round.length && !values.some(Boolean)} onClick={() => setRound([])}>Start again</button>
    </div>
  );
}

/** Scan look-alikes: their barcodes can't be read by eye - the scanner's screen shows what each one really is. */
export function Scan({ a, acts, value, attempt }) {
  const [scanned, setScanned] = useState(null);
  const shown = visible(acts).find((x) => x.id === scanned);
  return (
    <div className="mg-scan" role="group" aria-label={a.label}>
      <div className="mg-scan-row">
        {visible(acts).map((x, i) => (
          <button key={x.id} type="button" className={`mg-scan-pack ${scanned === x.id ? 'is-scanned' : ''} ${value === x.option ? 'is-picked' : ''}`}
            aria-label={`Scan pack ${i + 1}`} onClick={() => setScanned(x.id)}>
            <span className="mg-barcode" aria-hidden="true" style={{ '--seed': i }} />
          </button>
        ))}
      </div>
      <div className={`mg-scan-screen ${shown ? 'is-on' : ''}`} aria-live="polite">{shown ? `▸ ${shown.option}` : 'Scan one…'}</div>
      <button type="button" className="mg-btn" disabled={!shown} onClick={() => shown && attempt(shown)}>{a.label}</button>
    </div>
  );
}

const COINS = [20, 10, 5, 2, 1, 0.5, 0.2, 0.1, 0.05];
const money = (n) => n.toFixed(2);

/** Count out the change: tap notes and coins from the drawer onto the counter, then hand it over. */
export function CountMoney({ a, acts, value, attempt, text }) {
  const [counter, setCounter] = useState([]);
  const cur = text?.currency || '$';
  const total = Math.round(counter.reduce((n, c) => n + c, 0) * 100) / 100;
  const give = () => {
    const x = visible(acts).find((y) => Math.abs(parseFloat(y.option) - total) < 0.001) || other(acts);
    if (x) attempt(x);
  };
  return (
    <div className="mg-till" role="group" aria-label={a.label}>
      <div className="mg-till-drawer">
        {COINS.map((c) => (
          <button key={c} type="button" className={c >= 5 ? 'mg-note' : 'mg-coin'} aria-label={`Add ${cur}${money(c)}`}
            onClick={() => setCounter((k) => [...k, c])}>{c >= 1 ? `${cur}${c}` : `${Math.round(c * 100)}¢`}</button>
        ))}
      </div>
      <div className="mg-till-counter" aria-label="On the counter">
        {counter.map((c, i) => <span key={i} className={c >= 5 ? 'mg-note is-small' : 'mg-coin is-small'}>{c >= 1 ? c : Math.round(c * 100)}</span>)}
      </div>
      <div className="mg-till-total" aria-live="polite">{cur}{money(total)}</div>
      <div className="mg-folder-actions">
        <button type="button" className="mg-btn" disabled={!counter.length} onClick={give}>{a.label}</button>
        <button type="button" className="mg-link-btn" disabled={!counter.length} onClick={() => setCounter([])}>Put it back</button>
      </div>
      {value !== null && value !== undefined && <p className="mg-form-sent">Handed over: {cur}{value}</p>}
    </div>
  );
}

const DECAL = { dent: '', tear: '', stain: '', crack: '', leak: '💧', fire: '🔥', here: '📍' };

/** Tap the spot on the item's picture (its hotspots); what's drawn on it (damage, "you are here") is shown there. */
export function Point({ a, acts, value, attempt, src, asset, icon, hotspots = {}, marks = {} }) {
  const byName = Object.fromEntries(visible(acts).map((x) => [x.option, x]));
  return (
    <div className="mg-point" role="group" aria-label={a.label}>
      <span className="mg-inv-ask">{a.label}:</span>
      <div className="mg-point-picture">
        {src ? <img src={asset(src)} alt="" draggable={false} /> : <span className="mg-point-icon" aria-hidden="true">{icon}</span>}
        {Object.entries(hotspots).map(([name, [x, y, w, h]]) => (
          <React.Fragment key={name}>
            {marks[name] && <span className={`mg-decal is-${marks[name]}`} style={{ left: `${x}%`, top: `${y}%`, width: `${w}%`, height: `${h}%` }} aria-hidden="true">{DECAL[marks[name]]}</span>}
            {byName[name] && (
              <button type="button" className={`mg-hotspot ${value === name ? 'is-picked' : ''}`} aria-label={`${a.label}: ${name}`}
                aria-pressed={value === name} style={{ left: `${x}%`, top: `${y}%`, width: `${w}%`, height: `${h}%` }}
                onClick={() => attempt(byName[name])} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

const HOLD_MS = 1200;

/** Press and hold until it's done (lower the forks, pull the lever) - let go early and it stops. */
export function Hold({ a, acts, attempt }) {
  const [held, setHeld] = useState(0);         // 0..1
  const timer = useRef(null);
  const ms = a.seconds ? a.seconds * 1000 : HOLD_MS;
  const stop = () => { clearInterval(timer.current); timer.current = null; setHeld(0); };
  const start = (x) => {
    if (timer.current) return;
    const began = Date.now();
    timer.current = setInterval(() => {
      const f = Math.min(1, (Date.now() - began) / ms);
      setHeld(f);
      if (f >= 1) { clearInterval(timer.current); timer.current = null; attempt(x); }
    }, 60);
  };
  useEffect(() => () => clearInterval(timer.current), []);
  if (a.look === 'wipe') {
    // the spill shrinks while you hold - let go and it's still there
    return acts.map((x) => (
      <div key={x.id} className="mg-wipe">
        <div className="mg-wipe-floor" aria-hidden="true"><i className="mg-wipe-puddle" style={{ transform: `scale(${1 - held * 0.95})` }} /></div>
        <button type="button" className="mg-btn mg-hold-btn" onPointerDown={() => start(x)} onPointerUp={stop} onPointerLeave={stop}
          onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); attempt(x); } }}>
          <i style={{ width: `${held * 100}%` }} aria-hidden="true" /><span>{a.label}</span>
        </button>
      </div>
    ));
  }
  if (a.look === 'wash') {
    // the tap runs and the lather builds while held; the seconds count down - let go early and it starts again
    return acts.map((x) => (
      <div key={x.id} className={`mg-wash ${held > 0 ? 'is-running' : ''}`}>
        <div className="mg-wash-basin" aria-hidden="true">
          <i className="mg-wash-water" />
          <span className="mg-wash-hands" style={{ opacity: 0.4 + held * 0.6 }}>🙌</span>
          {Array.from({ length: Math.round(held * 8) }, (_, k) => <b key={k} className="mg-wash-bubble" style={{ left: `${10 + k * 11}%` }} />)}
        </div>
        <button type="button" className="mg-btn mg-hold-btn" onPointerDown={() => start(x)} onPointerUp={stop} onPointerLeave={stop}
          onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); attempt(x); } }}>
          <i style={{ width: `${held * 100}%` }} aria-hidden="true" />
          <span>{held > 0 ? `${Math.ceil((1 - held) * (a.seconds || 1))} s` : a.label}</span>
        </button>
      </div>
    ));
  }
  return acts.map((x) => (
    <button key={x.id} type="button" className="mg-btn mg-hold-btn" onPointerDown={() => start(x)} onPointerUp={stop} onPointerLeave={stop}
      onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); attempt(x); } }}>
      <i style={{ width: `${held * 100}%` }} aria-hidden="true" />
      <span>{a.label}</span>
    </button>
  ));
}

export const MECHANIC_VIEWS = { confirm: Confirm, sign: Sign, choose: Choose, compose: Compose, pick: Pick, place: Place, keypad: Keypad,
  spot: Spot, amount: Amount, pour: Pour, dial: Dial, hold: Hold,
  sort: Sort, switches: Switches, choose_several: ChooseSeveral, sequence: Sequence, scan: Scan, count_money: CountMoney, point: Point };

/** A document the player reads facts from - drawn as the real thing. */
export function Document({ doc, text, facts }) {
  if (doc === 'prescription') {
    return (
      <div className="mg-doc mg-doc-rx">
        <span className="mg-doc-rx-mark">℞</span>
        <p><Highlighted text={text} facts={facts} /></p>
        <span className="mg-doc-signature">Dr. ______</span>
      </div>
    );
  }
  if (doc === 'calendar') {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const marked = days.findIndex((d) => new RegExp(`\\b${d}`, 'i').test(text));
    return (
      <div className="mg-doc mg-doc-cal">
        <div className="mg-cal-grid">{days.map((d, i) => <span key={d} className={i === marked ? 'is-marked' : ''}>{d}</span>)}</div>
        <p className="mg-hand"><Highlighted text={text} facts={facts} /></p>
      </div>
    );
  }
  if (doc === 'screen') {
    // short rows ("Printer paper: 0 · Reorder amount: 5 reams") - a field and its value where there's a colon
    return (
      <div className="mg-doc mg-doc-screen" role="table">
        {linesOf(text).map((row) => {
          const at = row.indexOf(': ');
          return (
            <div key={row} role="row" className="mg-screen-row">
              {at > 0
                ? <><span role="cell">{row.slice(0, at)}</span><span role="cell"><Highlighted text={row.slice(at + 2)} facts={facts} /></span></>
                : <span role="cell"><Highlighted text={row} facts={facts} /></span>}
            </div>
          );
        })}
      </div>
    );
  }
  if (doc === 'id') {
    const [name, ...rest] = linesOf(text);
    return (
      <div className="mg-doc mg-doc-id">
        <span className="mg-id-photo" aria-hidden="true">🧑</span>
        <div><strong>{name}</strong>{rest.map((l) => <span key={l}><Highlighted text={l} facts={facts} /></span>)}</div>
      </div>
    );
  }
  if (doc === 'note') {
    return (
      <div className="mg-doc mg-doc-note">
        <span className="mg-note-head">Delivery note</span>
        {linesOf(text).map((row) => <p key={row}><Highlighted text={row} facts={facts} /></p>)}
      </div>
    );
  }
  if (doc === 'log') {
    return (
      <div className="mg-doc mg-doc-log">
        {linesOf(text).map((row) => <p key={row} className="mg-hand"><Highlighted text={row} facts={facts} /></p>)}
      </div>
    );
  }
  return <p className="mg-inv-says"><Highlighted text={text} facts={facts} /></p>;
}
